// src/components/imageCrop/ImageCropModal.jsx
//
// Shared square-crop step for any image upload flow: shows the picked image
// in a fixed-size crop viewport, lets the admin pan/zoom, then on confirm
// crops it to a square and compresses it under a byte budget before handing
// the resulting File back to the caller.

import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { getCroppedFile } from "utils/cropImage";

const ImageCropModal = ({ isOpen, onClose, imageSrc, fileName, onCropComplete }) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleCropComplete = useCallback((_croppedArea, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setIsProcessing(true);
    try {
      const file = await getCroppedFile(imageSrc, croppedAreaPixels, fileName);
      onCropComplete(file);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("crop_image_title")}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box position="relative" width="100%" height="300px" bg="gray.800">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="rect"
                showGrid
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={handleCropComplete}
              />
            )}
          </Box>
          <Text mt={4} mb={1} fontSize="sm">
            {t("crop_zoom_label")}
          </Text>
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.05}
            onChange={setZoom}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isProcessing}>
            {t("cancel")}
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleConfirm}
            isLoading={isProcessing}
            isDisabled={!croppedAreaPixels}
          >
            {t("crop_and_use")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImageCropModal;
